/**
 * InboxList - Left panel showing list of notifications
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DraftsIcon from '@mui/icons-material/Drafts';
import MarkunreadIcon from '@mui/icons-material/Markunread';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { InboxListItem } from './InboxListItem';
import type { InboxListProps } from './types';

// Re-export types
export type { InboxListProps } from './types';

const CATEGORY_EMPTY_LABELS: Record<string, string> = {
  tasks: 'No tasks yet',
  notifications: 'No notifications yet',
  incidents: 'No incidents yet',
  news: 'No news yet',
};

function getEmptyLabel(searchQuery?: string, categoryFilter?: string): string {
  if (searchQuery) return 'No communication match your search';
  if (categoryFilter)
    return CATEGORY_EMPTY_LABELS[categoryFilter] ?? 'No communication yet';
  return 'No communication yet';
}

function EmptyState({
  searchQuery,
  categoryFilter,
  hasActiveFilters,
  onClearFilters,
}: {
  searchQuery?: string;
  categoryFilter?: string;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}) {
  if (hasActiveFilters) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          gap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No exact results found.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try changing or removing some filters.
        </Typography>
        {onClearFilters && (
          <Button
            size="small"
            variant="outlined"
            onClick={onClearFilters}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            Clear all
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 4,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {getEmptyLabel(searchQuery, categoryFilter)}
      </Typography>
    </Box>
  );
}

function SelectionToolbar({
  selectionCount,
  allSelectedRead,
  allSelectedUnread,
  onBulkMarkRead,
  onBulkMarkUnread,
  onBulkDismiss,
  onClearSelection,
}: {
  selectionCount: number;
  allSelectedRead: boolean;
  allSelectedUnread: boolean;
  onBulkMarkRead: () => void;
  onBulkMarkUnread: () => void;
  onBulkDismiss: () => void;
  onClearSelection: () => void;
}) {
  if (selectionCount === 0) {
    return (
      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
        Inbox
      </Typography>
    );
  }
  return (
    <>
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
        {selectionCount} selected
      </Typography>
      <Tooltip title={allSelectedRead ? 'Mark as unread' : 'Mark as read'}>
        <IconButton
          size="small"
          onClick={allSelectedRead ? onBulkMarkUnread : onBulkMarkRead}
        >
          {allSelectedRead ? (
            <MarkunreadIcon fontSize="small" />
          ) : (
            <DraftsIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      {!allSelectedRead && !allSelectedUnread && (
        <Tooltip title="Mark as unread">
          <IconButton size="small" onClick={onBulkMarkUnread}>
            <MarkunreadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Hide">
        <IconButton size="small" onClick={onBulkDismiss} color="error">
          <VisibilityOffIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Clear selection">
        <IconButton size="small" onClick={onClearSelection}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
}

export function InboxList({
  items,
  loading,
  selectedId,
  onSelect,
  searchQuery,
  categoryFilter,
  page,
  pageSize,
  pageCount,
  showingRange,
  onPageChange,
  onPageSizeChange,
  checkedIds,
  onCheckToggle,
  onSelectAll,
  onClearSelection,
  onBulkMarkRead,
  onBulkMarkUnread,
  onBulkDismiss,
  hasActiveFilters,
  onClearFilters: _onClearFilters,
}: InboxListProps) {
  // The hook applies all filters server-side (search, labels, time, read/unread tab).
  // No client-side post-filtering needed here.
  const filteredItems = items;

  const allOnPageChecked =
    filteredItems.length > 0 && filteredItems.every(i => checkedIds.has(i.id));
  const someOnPageChecked = filteredItems.some(i => checkedIds.has(i.id));
  const selectionCount = checkedIds.size;

  const selectedItems = items.filter(i => checkedIds.has(i.id));
  const allSelectedUnread =
    selectedItems.length > 0 && selectedItems.every(i => !i.read);
  const allSelectedRead =
    selectedItems.length > 0 && selectedItems.every(i => i.read);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (filteredItems.length === 0 && !hasActiveFilters) {
    return (
      <EmptyState
        searchQuery={searchQuery}
        categoryFilter={categoryFilter ?? undefined}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 52,
        }}
      >
        <Checkbox
          size="small"
          checked={allOnPageChecked}
          indeterminate={someOnPageChecked && !allOnPageChecked}
          onChange={() =>
            allOnPageChecked ? onClearSelection() : onSelectAll()
          }
          sx={{ p: 0.25 }}
        />
        <SelectionToolbar
          selectionCount={selectionCount}
          allSelectedRead={allSelectedRead}
          allSelectedUnread={allSelectedUnread}
          onBulkMarkRead={onBulkMarkRead}
          onBulkMarkUnread={onBulkMarkUnread}
          onBulkDismiss={onBulkDismiss}
          onClearSelection={onClearSelection}
        />
      </Box>

      {/* Bulk action confirmation bar */}
      {selectionCount > 0 && <Divider />}

      {/* List */}
      <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {filteredItems.map((item, index) => (
          <InboxListItem
            key={item.id}
            notification={item}
            isSelected={item.id === selectedId}
            isMultiSelected={checkedIds.has(item.id)}
            onSelect={onSelect}
            onMultiSelectToggle={onCheckToggle}
            showDivider={index < filteredItems.length - 1}
          />
        ))}
      </List>

      {/* Footer with pagination */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Rows per page */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Rows per page:
          </Typography>
          <Select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            size="small"
            variant="standard"
            disableUnderline
            sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0 } }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </Box>

        {/* Range + prev/next */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {showingRange}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
